import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import * as Dialog from "@radix-ui/react-dialog";
import { useForm } from "@tanstack/react-form";
import { X } from "lucide-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { z } from "zod";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import {
    AudioRecorder,
    Avatar,
    Button,
    Combobox,
    FileAttachment,
    Input,
} from "components/elements";
import { ManageAttachmentsModal } from "components/elements/ManageAttachmentsModal";
import { OrganizationSelector } from "components/form/OrganizationSelector";
import { PostEditor } from "components/form/PostEditor";
import { TemplateList, resolveTemplate, useTemplateList } from "components/form/TemplatePicker";
import { UploadDocument } from "components/form/UploadDocument";
import { useUserRoleInfo } from "@compass/shared/hooks";
import { trpc } from "lib/trpc";
import { useStore } from "@tanstack/react-store";
import {
    ClientCurrentUser,
    ClientDocument,
    ClientUser,
    DiscussionCreate,
    DiscussionCreateSchema,
} from "@compass/shared/types";
import { useExternalOrgConfirm } from "./useExternalOrgConfirm";

type CreateDiscussionModalProps = {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    onSubmit: (value: DiscussionCreate) => void;
    attachments: ClientDocument[];
    setAttachments: Dispatch<SetStateAction<ClientDocument[]>>;
    currentUser: ClientCurrentUser;
    organizations: { id: number; name: string }[];
};

type WizardStep = "recipients" | "template" | "compose";

export const CreateDiscussionModal = ({
    isOpen,
    onClose,
    onSubmit,
    attachments,
    setAttachments,
    currentUser,
    organizations,
}: CreateDiscussionModalProps) => {
    // contentJson lives outside the tanstack form store: nesting a recursive
    // TipTapDoc type into form.defaultValues causes a "type instantiation is
    // excessively deep" error from tanstack/form's nested-key inference.
    const [messageContentJson, setMessageContentJson] = useState<TipTapDoc | null>(null);
    // Seeds the editor when a template is applied (compose step mounts fresh).
    const [messageInitialValue, setMessageInitialValue] = useState<TipTapDoc | null>(null);
    // Full user objects for everyone selected, captured at selection time so
    // the selected list survives org-filter changes — recipients can span many
    // organizations even though `usersList` only holds the current filter.
    const [selectedUserDetails, setSelectedUserDetails] = useState<Record<number, ClientUser>>({});

    const form = useForm({
        defaultValues: {
            title: "",
            users: [] as number[],
        },
        onSubmit: async ({ value }) => {
            if (!messageContentJson) return;
            const parsed = DiscussionCreateSchema.parse({
                ...value,
                message: {
                    contentJson: messageContentJson,
                    attachments: attachments.map((a) => a.id),
                },
            });
            onSubmit(parsed);
            resetAll();
        },
    });

    const [showManageAttachmentModal, setManageAttachmentModal] = useState(false);
    const [showUploadModal, setUploadModal] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<number | null | undefined>();
    const [step, setStep] = useState<WizardStep>("recipients");

    // role type and permissions
    const userRoleInfo = useUserRoleInfo(currentUser);
    // Compass users get the multi-step wizard (recipient pick → template →
    // compose). Everyone else keeps the single-panel form.
    const isWizard = userRoleInfo.canAddUsers;

    const compassOrg = useMemo(() => {
        return organizations.find((org) => org.name.toLowerCase() === "compass");
    }, [organizations]);

    // `users.list` doesn't return organizationName, but it does return
    // organizationId — resolve the display name from the org list we already
    // have so selected rows (and {{organization_name}}) show the org.
    const orgNameById = useMemo(
        () => new Map(organizations.map((o) => [o.id, o.name])),
        [organizations],
    );
    const orgNameForUser = (u: ClientUser): string | undefined =>
        u.organizationName ??
        (u.organizationId != null ? orgNameById.get(u.organizationId) : undefined);

    // default organization
    const effectiveOrganization =
        userRoleInfo.type === "basic" && compassOrg ? compassOrg.id : selectedOrganization;

    const usersListQuery = trpc.users.list.useQuery(
        { organizationId: effectiveOrganization ?? undefined },
        { enabled: userRoleInfo.canAddUsers },
    );

    const usersList = usersListQuery?.data?.users ?? [];

    const usersValue = useStore(form.store, (state) => state.values.users);

    const selectedUserIds = Array.isArray(usersValue) ? usersValue : [];

    // Resolved from the persisted detail map (not the org-filtered usersList),
    // so people picked from other orgs stay visible after the filter changes.
    const selectedUsers = useMemo(
        () =>
            selectedUserIds
                .map((id) => selectedUserDetails[id])
                .filter((u): u is ClientUser => Boolean(u)),
        [selectedUserIds, selectedUserDetails],
    );

    const recipientNames = useMemo(
        () => selectedUsers.map((u) => u.name).filter((name): name is string => Boolean(name)),
        [selectedUsers],
    );

    // Warns when a recipient would expand the discussion to a 2nd+ external
    // org (PRTL-1324). Own-org + 1 external is allowed silently.
    const { onBeforeAdd, modal: externalOrgConfirmModal } = useExternalOrgConfirm({
        existingUsers: selectedUsers,
    });

    /**
     * First non-Compass participant's org name — fills {{organization_name}}.
     * Walks the author + selected recipients in order; takes the first whose
     * role is not COMPASS_USER / COMPASS_SUPERUSER.
     */
    const firstNonCompassOrgName = useMemo(() => {
        const isCompassRole = (role: string | undefined) =>
            role === "COMPASS_USER" || role === "COMPASS_SUPERUSER";
        if (currentUser && !isCompassRole(currentUser.role)) {
            return currentUser.organizationName ?? undefined;
        }
        for (const u of selectedUsers) {
            if (isCompassRole(u.role)) continue;
            return orgNameForUser(u);
        }
        return undefined;
    }, [currentUser, selectedUsers, orgNameById]);

    // Templates for the template step (only fetched once we reach it).
    const { templates, isLoading: templatesLoading } = useTemplateList(
        "PRIVATE",
        isWizard && step === "template",
    );

    const addUser = async (userId: number | null | undefined) => {
        if (!userId || selectedUserIds.includes(userId)) return;
        const user = usersList.find((u) => u.id === userId);
        if (user) {
            const confirmed = await onBeforeAdd(user);
            if (!confirmed) return;
            setSelectedUserDetails((prev) => ({ ...prev, [userId]: user }));
        }
        form.setFieldValue("users", [...selectedUserIds, userId]);
    };

    const removeUser = (userId: number) => {
        form.setFieldValue(
            "users",
            selectedUserIds.filter((id) => id !== userId),
        );
        setSelectedUserDetails((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
        });
    };

    const userOptions = (query: string) => {
        const q = query.trim().toLowerCase();
        return usersList
            .filter(
                (u) =>
                    u.id !== currentUser?.id &&
                    u.isPrivateEnabled &&
                    !selectedUserIds.includes(u.id) &&
                    (q === "" ||
                        u.name?.toLowerCase().includes(q) ||
                        u.firstName?.toLowerCase().includes(q) ||
                        u.lastName?.toLowerCase().includes(q) ||
                        u.email.toLowerCase().includes(q)),
            )
            .map((u) => ({ value: u.id, display: u.name || u.email }));
    };

    const addAttachment = (file: ClientDocument) => {
        setAttachments((prev) => (prev.some((a) => a.id === file.id) ? prev : [...prev, file]));
    };

    const addAttachments = (files: ClientDocument[]) => {
        setAttachments((prev) => {
            const existingIds = new Set(prev.map((a) => a.id));
            const newFiles = files.filter((f) => !existingIds.has(f.id));
            return [...prev, ...newFiles];
        });
        setManageAttachmentModal(false);
    };

    const removeAttachment = (file: ClientDocument) => {
        setAttachments((prev) => prev.filter((att) => att.id !== file.id));
    };

    const onOrganizationChange = (value: number | null | undefined) => {
        if (userRoleInfo.canChangeOrg) {
            // Only changes which org the search filters by — selected users
            // (possibly from other orgs) are intentionally preserved.
            setSelectedOrganization(value);
        }
    };

    const resetAll = () => {
        form.reset();
        setMessageContentJson(null);
        setMessageInitialValue(null);
        setSelectedUserDetails({});
        setAttachments([]);
        setSelectedOrganization(undefined);
        setStep("recipients");
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) resetAll();
        onClose(open);
    };

    const applyTemplate = (template: Parameters<typeof resolveTemplate>[0]) => {
        const { subject, body } = resolveTemplate(template, {
            participantNames: recipientNames,
            organizationName: firstNonCompassOrgName,
            userFirstName: currentUser?.firstName,
            userLastName: currentUser?.lastName,
        });
        if (subject) form.setFieldValue("title", subject);
        setMessageInitialValue(body);
        setMessageContentJson(body);
        setStep("compose");
    };

    const startFromScratch = () => {
        setMessageInitialValue(null);
        setStep("compose");
    };

    // ---- Shared compose fields (used by wizard compose step + simple form) ----

    const subjectField = (
        <div className="flex w-full flex-col gap-1">
            <div className="font-semibold text-[#424242]">
                <span className="text-red-500">*</span> Subject
            </div>
            <form.Field
                name="title"
                validators={{
                    onSubmit: z
                        .string()
                        .max(100, "Title must be at most 100 characters long")
                        .refine((val) => val.trim().length > 0, {
                            message: "Title is required",
                        }),
                }}
            >
                {(field) => (
                    <>
                        <Input
                            name={field.name}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder="Subject..."
                            className="mt-1 h-9 rounded bg-[#f7f7f7] px-3 text-sm"
                        />
                        {field.state.meta.errors.length > 0 && (
                            <span className="mt-1 text-xs text-red-500">
                                {field.state.meta.errors[0]?.message}
                            </span>
                        )}
                    </>
                )}
            </form.Field>
        </div>
    );

    const messageField = (
        <div>
            <div className="mb-2 font-semibold text-[#424242]">
                <span className="text-red-500">*</span> Message
            </div>
            <PostEditor
                initialValue={messageInitialValue}
                onChange={(doc) => setMessageContentJson(doc)}
                className="mt-1 rounded border border-gray-300 text-sm"
            />
        </div>
    );

    const attachmentsField = (
        <>
            {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {attachments.map((attachment) => (
                        <FileAttachment
                            key={attachment.id}
                            file={attachment}
                            onRemove={removeAttachment}
                            className="h-8"
                        />
                    ))}
                </div>
            )}
            <div className="flex w-full items-center justify-between gap-2">
                <Button
                    type="button"
                    variant="secondary"
                    className="flex-1 text-sm"
                    onClick={() => setUploadModal(true)}
                >
                    New attachment
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    className="flex-1 text-sm"
                    onClick={() => setManageAttachmentModal(true)}
                >
                    Previous attachments
                </Button>
                <AudioRecorder onRecordingComplete={addAttachment} notifyContext="discussion" />
            </div>
            <ManageAttachmentsModal
                isOpen={showManageAttachmentModal}
                setIsOpen={setManageAttachmentModal}
                onAttach={addAttachments}
                alreadyAttachedIds={new Set(attachments.map((a) => a.id))}
                button={null}
                blur={true}
                showPrivateFiles={true}
                showPublicFiles={true}
            />
            <UploadDocument
                isOpen={showUploadModal}
                setIsOpen={setUploadModal}
                onUpload={addAttachment}
                button={null}
                title="Add New Attachments"
                blur={true}
                notifyContext="discussion"
            />
        </>
    );

    const heading =
        isWizard && step === "recipients"
            ? "Who would you like to start with?"
            : isWizard && step === "template"
              ? "Start with a template"
              : "Start a discussion";

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
            <Dialog.Content
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
                className="
                    fixed left-1/2 top-1/2 z-50
                    max-h-[92vh] min-h-[640px] w-[95vw] max-w-[1000px] -translate-x-1/2
                    -translate-y-1/2 overflow-y-auto
                    rounded-2xl
                    bg-white p-5
                    shadow-lg
                    sm:p-8
                "
            >
                <div className="flex items-center justify-between">
                    <div className="text-left align-middle font-sans text-[24px] font-bold leading-[100%] tracking-[0] text-[#424242]">
                        {heading}
                    </div>
                    <Dialog.Close>
                        <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                    </Dialog.Close>
                </div>
                <hr className="-mx-[24px] my-4 border-[#c8c8c8]" />

                <div className="max-h-[90vh] overflow-x-visible pr-1">
                    {/* ---------- WIZARD: STEP 1 — RECIPIENTS ---------- */}
                    {isWizard && step === "recipients" && (
                        <div className="flex min-h-[55vh] flex-col gap-4">
                            {/* Top row: org selector | user selector */}
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="flex flex-col gap-1">
                                    <div className="text-sm font-semibold text-[#424242]">
                                        Organization
                                    </div>
                                    {userRoleInfo.type === "advanced" ? (
                                        <OrganizationSelector
                                            inputClassName="h-10"
                                            withClear
                                            value={effectiveOrganization}
                                            onChange={onOrganizationChange}
                                            organizations={organizations}
                                            placeholder="Filter by organization"
                                        />
                                    ) : userRoleInfo.type === "basic" && compassOrg ? (
                                        <div className="flex h-10 items-center rounded bg-gray-100 px-3 text-sm text-gray-600">
                                            {compassOrg.name} (fixed)
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="text-sm font-semibold text-[#424242]">
                                        Add people
                                    </div>
                                    <Combobox
                                        placeholder="Search people..."
                                        inputClassName="h-10"
                                        value={null}
                                        withClear={false}
                                        onChange={addUser}
                                        displayValue={() => ""}
                                        options={userOptions}
                                    />
                                </div>
                            </div>

                            {/* Selected users list */}
                            <div className="flex-1">
                                <div className="mb-2 text-sm font-semibold text-[#424242]">
                                    {selectedUsers.length > 0
                                        ? `Selected (${selectedUsers.length})`
                                        : "No one selected yet"}
                                </div>
                                <div className="flex flex-col gap-2 overflow-y-auto">
                                    {selectedUsers.map((u) => (
                                        <div
                                            key={u.id}
                                            className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Avatar
                                                    photoUrl={u.avatarUrl}
                                                    name={u.name}
                                                    initials={u.initials}
                                                    className="h-10 w-10 shrink-0"
                                                />
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-[#424242]">
                                                        {u.name || u.email}
                                                    </div>
                                                    <div className="truncate text-xs text-gray-500">
                                                        {orgNameForUser(u) ?? "—"}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeUser(u.id)}
                                                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                                aria-label={`Remove ${u.name || u.email}`}
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-2 flex justify-between">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => handleOpenChange(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    disabled={selectedUsers.length === 0}
                                    onClick={() => setStep("template")}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* ---------- WIZARD: STEP 2 — TEMPLATE ---------- */}
                    {isWizard && step === "template" && (
                        <div className="flex flex-col gap-3">
                            <p className="text-sm text-gray-500">
                                Pick a template to pre-fill the subject and message, or start from a
                                blank discussion.
                            </p>
                            <TemplateList
                                templates={templates}
                                isLoading={templatesLoading}
                                onSelect={applyTemplate}
                            />
                            <div className="mt-2 flex justify-between">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setStep("recipients")}
                                >
                                    Back
                                </Button>
                                <Button type="button" variant="primary" onClick={startFromScratch}>
                                    Start from scratch
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* ---------- WIZARD: STEP 3 / SIMPLE FORM — COMPOSE ---------- */}
                    {(!isWizard || step === "compose") && (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                form.handleSubmit();
                            }}
                            className="flex flex-col gap-3"
                        >
                            {subjectField}
                            {messageField}
                            {attachmentsField}

                            {/* Read-only user message */}
                            {userRoleInfo.type === "readonly" && (
                                <div className="text-xs text-gray-500">
                                    This discussion will be visible only to you and Compass users.
                                </div>
                            )}

                            {/* Basic user message */}
                            {userRoleInfo.type === "basic" && (
                                <div className="text-xs text-gray-500">
                                    This discussion will be shared within the Compass organization.
                                </div>
                            )}

                            <div className="flex justify-between">
                                {isWizard ? (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => setStep("template")}
                                    >
                                        Back
                                    </Button>
                                ) : (
                                    <span />
                                )}
                                <Button type="submit" variant="primary">
                                    Create discussion <SubmitIcon className="ml-2" />
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
                {externalOrgConfirmModal}
            </Dialog.Content>
        </Dialog.Root>
    );
};
